import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isProblemError } from '../../api/client';
import { useGetMe, useReauthenticate } from '../../api/generated/identity/identity';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Dialog } from '../../design/primitives/Dialog';
import { Field } from '../../design/primitives/Field';
import { formatInPersonTimezone } from '../../i18n/datetime';
import styles from './reauth.module.css';

/**
 * O 403 que a Onda 0B devolve em rota sensivel, reconhecido pelo titulo.
 *
 * Fica exportado porque quem chama precisa **nao** desenhar o alerta generico
 * de erro para este caso: o dialogo ja e a resposta, e dois avisos ao mesmo
 * tempo sobre a mesma falha e ruido.
 */
export function isReauthRequired(error: unknown): boolean {
  return isProblemError(error) && error.status === 403 && /reautentica/i.test(error.title);
}

type Attempt = () => void;

export interface ReauthGate {
  /**
   * Executa a acao. O argumento que ela recebe e o `onError` a repassar para a
   * mutacao: e por ele que o portao descobre o 403 e guarda **a mesma acao**
   * para repetir depois da confirmacao.
   */
  readonly run: (action: (onReauthRequired: (error: unknown) => void) => void) => void;
  /** Onde o dialogo e o aviso da janela aparecem. Renderize uma vez por tela. */
  readonly dialog: ReactNode;
}

/**
 * Reautenticacao que **retoma a acao de onde parou**.
 *
 * Antes de `reauthenticate` existir, onze operacoes sensiveis respondiam 403
 * para sempre e a tela mandava a pessoa entrar de novo — perdendo o formulario
 * preenchido, que e o defeito que faz alguem desistir de estornar. Aqui a acao
 * recusada e guardada como esta, com os valores que ja estavam no formulario, e
 * roda de novo assim que a credencial e reconfirmada.
 *
 * A acao guardada e a propria funcao que falhou, e nao uma copia dos campos:
 * assim nao existe uma segunda fonte de verdade do que sera reenviado.
 */
export function useReauthGate(): ReauthGate {
  const [pending, setPending] = useState<Attempt | null>(null);
  const [confirmedUntil, setConfirmedUntil] = useState<string | null>(null);

  /**
   * O aviso da janela morre junto com a janela.
   *
   * Sem isto, o alerta verde "a confirmacao vale ate HH:MM" fica na tela depois
   * do horario que ele proprio mostra, afirmando que a credencial esta
   * confirmada quando ja nao esta. Quem confia nele clica numa acao sensivel,
   * leva 403 e ve o dialogo abrir com o aviso de sucesso ainda ao lado — duas
   * informacoes opostas sobre o mesmo estado, e a errada e a mais visivel.
   *
   * O prazo vem do servidor em `expiresAt`, entao o relogio local so decide
   * QUANDO apagar, nunca ate quando vale. Instante ilegivel produz `NaN`, o
   * `setTimeout` dispara na hora e o aviso some: falha para o lado de nao
   * prometer nada.
   */
  useEffect(() => {
    if (confirmedUntil === null) return undefined;
    const remaining = new Date(confirmedUntil).getTime() - Date.now();
    // Prazo ja vencido — ou ilegivel, que produz NaN — agenda para o proximo
    // tique em vez de chamar setState aqui dentro. Mudar estado na propria
    // passagem do efeito obriga o React a renderizar duas vezes e o lint acusa,
    // com razao; um timeout nao-positivo dispara no tique seguinte, que e o
    // mesmo efeito visivel sem a renderizacao extra.
    const timer = setTimeout(() => setConfirmedUntil(null), remaining > 0 ? remaining : 0);
    return () => clearTimeout(timer);
  }, [confirmedUntil]);

  const run = useCallback<ReauthGate['run']>((action) => {
    const attempt = (): void => {
      action((error) => {
        if (isReauthRequired(error)) setPending(() => attempt);
      });
    };
    attempt();
  }, []);

  const dialog = (
    <>
      {pending !== null ? (
        <ReauthDialog
          onCancel={() => setPending(null)}
          onConfirmed={(expiresAt) => {
            const resume = pending;
            setPending(null);
            setConfirmedUntil(expiresAt);
            resume();
          }}
        />
      ) : null}
      {confirmedUntil !== null ? <ReauthWindowNotice expiresAt={confirmedUntil} /> : null}
    </>
  );

  return { run, dialog };
}

/**
 * Confirmacao da credencial.
 *
 * Tres decisoes vem da descricao da operacao no contrato, e nenhuma delas e
 * escolha de layout:
 *
 * - **Nao ha campo de e-mail.** O corpo nao tem o campo, e `getMe` nao devolve
 *   e-mail. Pedir um identificador que ninguem confere seria teatro.
 * - **Credencial recusada responde 422, e nao 401.** De proposito: o
 *   interceptador de 401 derrubaria a sessao — e o formulario junto, que e
 *   exatamente o que este dialogo existe para preservar. A mensagem e unica e
 *   nao diz se falhou a senha ou o codigo, porque a distincao conta a quem
 *   tenta qual metade ja acertou.
 * - **A janela nao e cinco minutos.** A duracao e configuracao do servidor e
 *   chega em `expiresAt`; a tela mostra o instante que recebeu.
 */
function ReauthDialog({
  onCancel,
  onConfirmed,
}: {
  readonly onCancel: () => void;
  readonly onConfirmed: (expiresAt: string) => void;
}) {
  const { t } = useTranslation();
  const me = useGetMe().data;
  const reauth = useReauthenticate();

  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const refused = isProblemError(reauth.error) && reauth.error.status === 422;
  const throttled = isProblemError(reauth.error) && reauth.error.status === 429;
  const unexpected = reauth.error !== null && !refused && !throttled;

  // O codigo e obrigatorio para quem tem inscricao confirmada, e omitido por
  // quem nao tem. Perguntar a `Me` evita mandar um campo que o servidor recusa.
  const needsCode = me?.mfaEnrolled === true;
  const canSubmit =
    password.length > 0 && (!needsCode || /^[0-9]{6}$/.test(totpCode)) && !reauth.isPending;

  const submit = (): void => {
    reauth.mutate(
      { data: { password, ...(needsCode ? { totpCode } : {}) } },
      {
        onSuccess: (result) => {
          setPassword('');
          setTotpCode('');
          onConfirmed(result.expiresAt);
        },
      },
    );
  };

  return (
    <Dialog
      open
      title={t('reauth.title')}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={reauth.isPending}>
            {t('action.cancel')}
          </Button>
          <Button variant="primary" onClick={submit} loading={reauth.isPending} disabled={!canSubmit}>
            {t('reauth.confirm')}
          </Button>
        </>
      }
    >
      <p>{t('reauth.detail')}</p>
      {/*
        Dizer que o formulario continua ali e o que impede a pessoa de fechar o
        dialogo e recomecar do zero por achar que perdeu o que digitou.
      */}
      <p className={styles.meta}>{t('reauth.formKept')}</p>

      {refused ? <Alert tone="error">{t('reauth.refused')}</Alert> : null}
      {throttled ? <Alert tone="warning">{t('reauth.throttled')}</Alert> : null}
      {unexpected ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

      <Field
        label={t('reauth.password')}
        type="password"
        required
        autoComplete="current-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />

      {needsCode ? (
        <Field
          label={t('reauth.totpCode')}
          hint={t('reauth.totpHint')}
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={totpCode}
          onChange={(event) => setTotpCode(event.target.value)}
        />
      ) : null}
    </Dialog>
  );
}

/** Ate quando a janela vale — o instante que o servidor devolveu, e nao um prazo fixo. */
function ReauthWindowNotice({ expiresAt }: { readonly expiresAt: string }) {
  const { t } = useTranslation();
  const me = useGetMe().data;
  if (!me) return null;

  return (
    <Alert tone="success">
      {t('reauth.confirmed', {
        time: formatInPersonTimezone(expiresAt, me.timezone, me.locale),
      })}
    </Alert>
  );
}
